import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import './Pages.css';

const ClientBookingFlow = ({ apiUrl, token }) => {
  const [step, setStep] = useState(1);
  const [services, setServices] = useState([]);
  const [selectedService, setSelectedService] = useState(null);
  const [bookingDate, setBookingDate] = useState('');
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [recordingRequested, setRecordingRequested] = useState(false);
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponError, setCouponError] = useState('');
  const [couponChecking, setCouponChecking] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const preselectedServiceId = location.state?.serviceId;
  const preselectedCoupon = location.state?.couponCode;

  useEffect(() => {
    fetchServices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (preselectedServiceId && services.length > 0) {
      const match = services.find((s) => s.id === preselectedServiceId);
      if (match) handleSelectService(match);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preselectedServiceId, services]);

  const fetchServices = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${apiUrl}/services`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setServices(response.data);
    } catch (err) {
      setError('Failed to load services');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectService = (service) => {
    setSelectedService(service);
    setRecordingRequested(false);
    setAppliedCoupon(null);
    setCouponError('');
    setCouponCode(preselectedCoupon || '');
    setStep(2);
  };

  const checkCoupon = async (codeOverride) => {
    const code = (codeOverride || couponCode).trim();
    if (!code || !selectedService) return;
    setCouponChecking(true);
    setCouponError('');
    try {
      const response = await axios.get(`${apiUrl}/coupons/validate`, {
        params: { code, service_id: selectedService.id },
        headers: { Authorization: `Bearer ${token}` }
      });
      setAppliedCoupon(response.data);
      setCouponCode(response.data.code);
    } catch (err) {
      setAppliedCoupon(null);
      setCouponError(err.response?.data?.error || 'Failed to validate coupon');
    } finally {
      setCouponChecking(false);
    }
  };

  // A coupon carried in via a short URL (e.g. from an email campaign) is
  // applied automatically as soon as its service is selected.
  useEffect(() => {
    if (preselectedCoupon && selectedService && step === 2 && !appliedCoupon) {
      checkCoupon(preselectedCoupon);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preselectedCoupon, selectedService, step]);

  const removeCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode('');
    setCouponError('');
  };

  const fetchSlots = async (date) => {
    if (!date || !selectedService) return;
    setSelectedSlot(null);
    setSlotsLoading(true);
    try {
      const response = await axios.get(
        `${apiUrl}/practitioners/${selectedService.practitioner_id}/availability`,
        {
          params: { date, service_id: selectedService.id },
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setSlots(response.data.slots || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load available times');
      setSlots([]);
    } finally {
      setSlotsLoading(false);
    }
  };

  const handleDateChange = (date) => {
    setBookingDate(date);
    fetchSlots(date);
  };

  const handleRequestBooking = async (e) => {
    e.preventDefault();
    if (!selectedService || !selectedSlot) {
      alert('Please choose an available time');
      return;
    }

    try {
      const response = await axios.post(
        `${apiUrl}/bookings`,
        {
          service_id: selectedService.id,
          booking_date: selectedSlot,
          recording_requested: recordingRequested,
          coupon_code: appliedCoupon ? appliedCoupon.code : undefined
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setBooking(response.data);
      setStep(3);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create booking');
    }
  };

  if (loading && !selectedService) {
    return <div className="page-container"><p className="text-muted">Loading services...</p></div>;
  }

  const progressLabels = ['Select Service', 'Choose Date/Time', 'Approval & Payment', 'Confirmed'];

  return (
    <div className="page-container" style={{ maxWidth: 640 }}>
      <div style={{ marginBottom: 28 }}>
        <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
          {[1, 2, 3, 4].map((s) => (
            <div
              key={s}
              style={{
                height: 6,
                flex: 1,
                borderRadius: 4,
                background: s <= step ? '#c2366f' : '#e9e9f0'
              }}
            />
          ))}
        </div>
        <div className="flex-between" style={{ fontSize: '0.78rem' }}>
          {progressLabels.map((l) => (
            <span key={l} className="text-muted">{l}</span>
          ))}
        </div>
      </div>

      {step === 1 && (
        <div>
          <h1 style={{ marginBottom: 20 }}>Select a Service</h1>
          {error && <div className="error-message">{error}</div>}
          {services.length === 0 ? (
            <p className="empty-state">No services available right now</p>
          ) : (
            services.map((service) => (
              <div
                key={service.id}
                onClick={() => handleSelectService(service)}
                className="card"
                style={{ cursor: 'pointer' }}
              >
                <div className="card-header" style={{ marginBottom: 6 }}>{service.name}</div>
                {service.practitioner_name && (
                  <p className="text-muted" style={{ marginTop: -4, marginBottom: 8, fontSize: '0.85rem' }}>
                    with {service.practitioner_name}
                  </p>
                )}
                <p className="text-muted">{service.description}</p>
                <div style={{ display: 'flex', gap: 20, marginTop: 12 }}>
                  <span style={{ fontWeight: 700, color: '#c2366f' }}>${parseFloat(service.price).toFixed(2)}</span>
                  <span className="text-muted">{service.duration_minutes} minutes</span>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {step === 2 && selectedService && (
        <div>
          <h1 style={{ marginBottom: 20 }}>Choose Date &amp; Time</h1>
          <div className="card">
            <div className="card-header">{selectedService.name}</div>
            {selectedService.practitioner_name && (
              <p className="text-muted" style={{ marginTop: -8, marginBottom: 14, fontSize: '0.85rem' }}>
                with {selectedService.practitioner_name}
                {selectedService.practitioner_bio && ` · ${selectedService.practitioner_bio}`}
              </p>
            )}
            <form onSubmit={handleRequestBooking}>
              <div className="form-group">
                <label>Date</label>
                <input
                  type="date"
                  required
                  value={bookingDate}
                  onChange={(e) => handleDateChange(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>

              {bookingDate && (
                <div className="form-group">
                  <label>Available Times</label>
                  {slotsLoading ? (
                    <p className="text-muted">Checking availability...</p>
                  ) : slots.length === 0 ? (
                    <p className="text-muted">No open times on this date - try another day.</p>
                  ) : (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      {slots.map((slot) => (
                        <button
                          type="button"
                          key={slot}
                          onClick={() => setSelectedSlot(slot)}
                          className={`btn btn-sm ${selectedSlot === slot ? 'btn-primary' : 'btn-secondary'}`}
                        >
                          {new Date(slot).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {selectedService.recordable && (
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 16, fontSize: '0.85rem' }}>
                  <input
                    type="checkbox"
                    checked={recordingRequested}
                    onChange={(e) => setRecordingRequested(e.target.checked)}
                    style={{ marginTop: 3 }}
                  />
                  <span>
                    I request this session be recorded and available to me, and consent to the{' '}
                    <a href="/legal/terms" target="_blank" rel="noopener noreferrer">Terms &amp; Conditions</a> associated with the recording.
                  </span>
                </label>
              )}
              <div className="form-group">
                <label>Coupon Code (optional)</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input
                    value={couponCode}
                    onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                    placeholder="e.g. WELCOME10"
                    style={{ flex: 1 }}
                  />
                  {appliedCoupon ? (
                    <button type="button" className="btn btn-secondary btn-sm" onClick={removeCoupon}>Remove</button>
                  ) : (
                    <button type="button" className="btn btn-secondary btn-sm" onClick={() => checkCoupon()} disabled={!couponCode.trim() || couponChecking}>
                      {couponChecking ? 'Checking...' : 'Apply'}
                    </button>
                  )}
                </div>
                {couponError && <p className="text-muted" style={{ color: '#dc2626', fontSize: '0.85rem', marginTop: 4 }}>{couponError}</p>}
                {appliedCoupon && (
                  <p style={{ color: '#16a34a', fontSize: '0.85rem', marginTop: 4 }}>
                    "{appliedCoupon.code}" applied - you saved ${appliedCoupon.discount_amount.toFixed(2)}
                  </p>
                )}
              </div>
              <div className="flex-between" style={{ background: '#fbe9f0', padding: 14, borderRadius: 10, marginBottom: 16 }}>
                <span>Total Cost:</span>
                <span style={{ fontSize: '1.3rem', fontWeight: 800, color: '#c2366f' }}>
                  {appliedCoupon ? (
                    <>
                      <span style={{ textDecoration: 'line-through', opacity: 0.5, fontSize: '1rem', marginRight: 8 }}>
                        ${parseFloat(selectedService.price).toFixed(2)}
                      </span>
                      ${appliedCoupon.final_amount.toFixed(2)}
                    </>
                  ) : (
                    `$${parseFloat(selectedService.price).toFixed(2)}`
                  )}
                </span>
              </div>
              <button type="submit" className="btn btn-primary btn-block" style={{ marginBottom: 10 }} disabled={!selectedSlot}>
                Request Booking
              </button>
              <button
                type="button"
                onClick={() => { setStep(1); setSelectedService(null); setBookingDate(''); setSlots([]); setSelectedSlot(null); }}
                className="btn btn-secondary btn-block"
              >
                Back
              </button>
            </form>
          </div>
        </div>
      )}

      {step === 3 && booking && (
        <div>
          <h1 style={{ marginBottom: 20 }}>Booking Requested</h1>
          <div className="card">
            <p style={{ marginBottom: 16 }}>
              Your request has been sent to the practitioner. Once approved, an invoice will appear
              here and under <strong>Invoices</strong> for you to pay - your session is confirmed as soon as payment goes through.
            </p>
            <div className="flex-between" style={{ background: '#f5f6fa', padding: 14, borderRadius: 10, marginBottom: 16 }}>
              <span>Service:</span>
              <strong>{selectedService.name}</strong>
            </div>
            <button onClick={() => navigate(`/bookings/${booking.id}`)} className="btn btn-primary btn-block">
              View Booking Status
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientBookingFlow;
